// The instructor watching a student session live from the class dashboard
// (byj-1wv.51.6), beside the rows rather than on the session's own page:
// what the student's terminal shows, through the share's screen socket, or
// the session's timeline, through its frame socket, as the share's page
// draws them — read-only, the instructor being a coach of every share to the
// class. A watched session holds its frame socket — the timeline's, and what
// a note to the student goes down — and, on the Screen tab, the screen's too,
// let go as the share ends, as the share's page lets it go. Its head says
// whose it is and how it stands, from the dashboard's own row, and opens the
// session's page, where the instructor can also request a turn; under it a
// note to the student, which the agent never sees, written in the share
// page's own card (ChatForm); Pin keeps it watched while others are picked
// (usePins).
import { Suspense, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { lazyChunk } from "../chunk";
import { api, wsURL, type ClassAlert, type ClassSession, type ShareDetail, type User } from "../api";
import { alertChips, byPressing, SaidByState, sessionLabel, shownState, type SeenMark } from "../classroom";
import { useNow } from "../now";
import { AlertIcon, alertTone } from "./AlertIcon";
import { useKept, useKeptChoice } from "../kept";
import { LiveConnection, type ConnState, type SocketLike } from "../live";
import { applyFrame, buildTimeline, canNote, emptySession, namesOf, withSentNote, type SessionState } from "../timeline";
import { frame, type Actor, type Envelope } from "../protocol";
import { t, ago, money, word } from "../i18n";
import { ChatForm } from "./Chat";
import { Link } from "./Nav";
import { panelId, tabId, Tabs } from "./Tabs";
import { Timeline } from "./Timeline";
import { VSplit } from "./VSplit";

const Screen = lazyChunk(() => import("./Screen"));

/** How many sessions an instructor may pin. */
export const MaxPins = 4;

type Tab = "screen" | "timeline";
const tabs: Tab[] = ["screen", "timeline"];

/**
 * The shares pinned on a class's dashboard, kept per browser; at most MaxPins.
 * `keep` drops the pins `listed` says no, so a share the list no longer has (a
 * removed student's, one gone) does not hold a pin no one can see to unpin.
 */
export function usePins(classId: string): [string[], (share: string) => void, (listed: (share: string) => boolean) => void] {
  const [pins, setPins] = useKept<string[]>(
    `class.pins.${classId}`,
    [],
    (s) => {
      const v = JSON.parse(s) as unknown;
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").slice(0, MaxPins) : undefined;
    },
    (v) => JSON.stringify(v),
  );
  const toggle = (share: string) =>
    setPins(pins.includes(share) ? pins.filter((x) => x !== share) : pins.length < MaxPins ? [...pins, share] : pins);
  const keep = (listed: (share: string) => boolean) => {
    const kept = pins.filter(listed);
    if (kept.length !== pins.length) setPins(kept);
  };
  return [pins, toggle, keep];
}

/**
 * How far the instructor marked each session's alerts seen (classroom
 * `SeenMark`), kept per class in this browser (byj-1wv.51.10), read by the
 * dashboard and the assignment page alike. Another browser, or another
 * instructor, has its own (RFC-043 "Known gaps"). `keep` drops the shares a
 * list no longer has, as `usePins` does, so it does not grow for good.
 */
export function useSeenAlerts(
  classId: string,
): [Record<string, SeenMark>, (share: string, mark: SeenMark) => void, (listed: (share: string) => boolean) => void] {
  const [seen, setSeen] = useKept<Record<string, SeenMark>>(
    `class.alerts-seen.${classId}`,
    {},
    (raw) => {
      const v = JSON.parse(raw) as unknown;
      if (!v || typeof v !== "object" || Array.isArray(v)) return undefined;
      const ok = (m: unknown): m is SeenMark =>
        !!m && typeof m === "object" && (typeof (m as SeenMark).seq === "number" || typeof (m as SeenMark).at === "string");
      return Object.fromEntries(Object.entries(v).filter((e): e is [string, SeenMark] => ok(e[1])));
    },
    (v) => JSON.stringify(v),
  );
  const keep = (listed: (share: string) => boolean) => {
    const kept = Object.entries(seen).filter(([share]) => listed(share));
    if (kept.length !== Object.keys(seen).length) setSeen(Object.fromEntries(kept));
  };
  return [seen, (share, mark) => setSeen({ ...seen, [share]: mark }), keep];
}

type Action = { type: "frame"; e: Envelope } | { type: "note"; author: Actor; text: string } | { type: "reopened" };

function reduce(s: SessionState, a: Action): SessionState {
  switch (a.type) {
    case "frame":
      return applyFrame(s, a.e);
    case "note":
      return withSentNote(s, a.author, a.text);
    case "reopened":
      // live again (its student resumed it): what was said stays, what comes next follows it
      return { ...s, closed: undefined };
  }
}

/** How many times a watch reads a share again for its first screen, as turns go by without one. */
const ScreenChecks = 3;

/**
 * A watched share's frames, live, from the start of what the relay keeps, as
 * a coach; `note`, which sends one down the same socket (false when it is
 * not open); and `reopen`, for a share live again under the same id after it
 * ended — the connection, stopped by its end, started again from its last
 * seq, as the share's page does.
 */
function useShareFeed(share: ShareDetail | null, me: User, socket?: (url: string) => SocketLike) {
  const [state, dispatch] = useReducer(reduce, emptySession);
  const [conn, setConn] = useState<ConnState>({ kind: "connecting" });
  const live = useRef<LiveConnection | null>(null);
  const id = share?.id;
  const actor: Actor = useMemo(() => ({ kind: "coach", user: me.id }), [me.id]);
  useEffect(() => {
    if (!id) return;
    const c = new LiveConnection({
      url: (since) => wsURL(`/v1/shares/${encodeURIComponent(id)}/ws?since=${since}`),
      me: actor,
      share: id,
      onFrame: (e) => dispatch({ type: "frame", e }),
      onState: setConn,
      onBadFrame: (err) => console.warn("watch: refused a frame", err),
      socket,
    });
    live.current = c;
    c.start();
    return () => {
      live.current = null;
      c.stop("left");
    };
  }, [id, actor, socket]);
  const note = useCallback(
    (text: string) => {
      const ok = (id && live.current?.send(frame("note", actor, { text }, id))) ?? false;
      if (ok) dispatch({ type: "note", author: actor, text });
      return !!ok;
    },
    [id, actor],
  );
  const reopen = useCallback(() => {
    dispatch({ type: "reopened" });
    live.current?.start();
  }, []);
  return { state, conn, note, reopen };
}

function WatchTimeline({ share, me, state }: { share: ShareDetail; me: User; state: SessionState }) {
  const turns = useMemo(() => buildTimeline(state.events), [state.events]);
  const names = useMemo(() => namesOf(share.owner, state.participants, me), [share.owner, state.participants, me]);
  return <Timeline turns={turns} names={names} empty={share.backlog_floor ? t("session:noEventsKept") : undefined} />;
}

export function SessionWatch({ session: s, me, pinned, canPin, onPin, onAssign, alerts = [], onSeen, onClose, load = api.share, socket }: {
  session: ClassSession;
  me: User;
  /** Given, the bar says its assignment and sets it (the class has some to set). */
  onAssign?: () => void;
  /** The alerts of its that still stand (classroom.currentAlerts), each kind said in full under its head. */
  alerts?: ClassAlert[];
  /** Given, Seen marks them seen: they leave its row, and a new one brings it back. */
  onSeen?: () => void;
  pinned: boolean;
  /** Whether another pin is allowed: fewer than MaxPins are. */
  canPin: boolean;
  onPin: () => void;
  /** Its ✕: stops watching it (a pinned one, unpinned). */
  onClose: () => void;
  /** The share's detail, for tests. */
  load?: (id: string) => Promise<ShareDetail>;
  /** A socket factory for the timeline, for tests. */
  socket?: (url: string) => SocketLike;
}) {
  const [share, setShare] = useState<ShareDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useKeptChoice<Tab>("class.watch.tab", "screen", tabs);
  const shareRef = useRef(share);
  shareRef.current = share;
  const checks = useRef(0);
  // the latest read of the detail is the one that counts: a first load still
  // on its way does not undo a re-read sent after it
  const asked = useRef(0);
  // its detail, from nothing; an error said only while there is nothing to show
  useEffect(() => {
    setShare(null);
    setError(null);
    checks.current = 0;
    const n = ++asked.current;
    let on = true;
    load(s.share).then(
      (d) => on && n === asked.current && setShare(d),
      (err: unknown) => on && n === asked.current && setError(err instanceof Error ? err.message : String(err)),
    );
    return () => {
      on = false;
    };
  }, [s.share, load]);
  // read again, for what the detail it was opened with lacks: a watch that
  // has its share keeps it if this fails
  const readAgain = useCallback(() => {
    const n = ++asked.current;
    load(s.share).then(
      (d) => {
        if (n !== asked.current) return;
        setShare(d);
        setError(null);
      },
      () => {},
    );
  }, [s.share, load]);
  const feed = useShareFeed(share, me, socket);
  const { reopen } = feed;
  const ended = feed.state.closed !== undefined;
  // its student resumed it: the share's frames ended (share.close stopped the
  // connection), and the row has since said it is live with something new —
  // the detail read again, and the frames followed again. Only a connection
  // that ended is started again, never one still open or waiting to retry;
  // and what the row said last as it ended is the mark, not a clock, so the
  // relay's time and this browser's need not agree
  const lastAt = useRef(s.last_event_at);
  lastAt.current = s.last_event_at;
  const lastAtEnd = useRef<string | null>(null);
  // taken as it ends: what the row says after is what is compared with it
  useEffect(() => {
    lastAtEnd.current = ended ? lastAt.current : null;
  }, [ended]);
  useEffect(() => {
    if (!ended || !s.live || lastAtEnd.current === null || s.last_event_at === lastAtEnd.current) return;
    lastAtEnd.current = null;
    checks.current = 0;
    readAgain();
    reopen();
  }, [ended, s.live, s.last_event_at, readAgain, reopen]);
  // a turn went by while it had no screen yet: its first screen may have
  // come since — asked a few times, not at every turn for as long as it is watched
  const turnsSeen = useRef(s.turns);
  useEffect(() => {
    const moved = s.turns !== turnsSeen.current;
    turnsSeen.current = s.turns;
    const cur = shareRef.current;
    if (!moved || !cur || cur.screen || !s.live || checks.current >= ScreenChecks) return;
    checks.current++;
    readAgain();
  }, [s.turns, s.live, readAgain]);
  // a screen to watch while the share is live: the relay lets it go as the
  // share ends, and a Screen kept would reconnect for as long as it is watched
  const hasScreen = share?.screen === true && s.live && feed.state.closed === undefined;
  const shown: Tab = hasScreen ? tab : "timeline";
  const policy = feed.state.policy ?? share?.policy;
  const open = feed.conn.kind === "open" && feed.state.closed === undefined;
  // named as its row names it, from the card alone, so the two match side by side
  const title = sessionLabel(s.title, s.share).text;
  const state = shownState(s);
  const now = useNow(alerts.length > 0);
  const kinds = alertChips(alerts).sort((a, b) => byPressing(a.kind, b.kind));
  const prefix = `watch-${s.share}`;

  return (
    <section
      className={`session-watch${pinned ? " is-pinned" : ""}`}
      aria-label={t("classes:watching", { name: s.student.name })}
      data-testid="session-watch"
      data-share={s.share}
      onKeyDown={(e) => {
        // not an Esc in the note box: a note half written is not dropped with the watch
        if (e.key !== "Escape" || pinned || e.defaultPrevented) return;
        if (e.target instanceof Element && e.target.closest("textarea, input")) return;
        e.preventDefault();
        onClose();
      }}
    >
      {/* whose it is, with its ✕; the assignment it works on, its title whole
          over two lines at most, ✎ to change it; how it stands; then its tabs,
          with Pin and Open */}
      <header className="session-watch-head">
        <div className="session-watch-title">
          <div className="grow">
            <h2 className="flush">{s.student.name}</h2>
            {onAssign ? (
              <button type="button" className="watch-assignment" aria-label={t("classes:setAssignmentOf", { name: s.student.name })} title={t("classes:setAssignment")} onClick={onAssign}>
                <span className={`watch-assignment-title${s.assignment ? "" : " muted"}`}>{s.assignment?.title ?? t("classes:noAssignmentChip")}</span>
                <span className="watch-assignment-edit" aria-hidden="true">
                  ✎
                </span>
              </button>
            ) : (
              s.assignment && <div className="watch-assignment-title">{s.assignment.title}</div>
            )}
            <div className="muted small ellipsis" title={title}>
              {title} · <span className={`state-chip-${state}`}>{word("classes:stateBadge", state) ?? state}</span> · {t("classes:turnsN", { count: s.turns })} · {money(s.cost_usd)}
            </div>
          </div>
          <button type="button" className="small" aria-label={t("classes:stopWatching")} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="session-watch-bar">
          {hasScreen ? (
            <Tabs
              prefix={prefix}
              label={t("classes:watching", { name: s.student.name })}
              value={shown}
              onChange={setTab}
              tabs={[
                { id: "screen", label: t("session:tabScreen") },
                { id: "timeline", label: t("session:tabTimeline") },
              ]}
            />
          ) : (
            <span className="muted small">{t("session:tabTimeline")}</span>
          )}
          <span className="grow" />
          <button type="button" className={`small${pinned ? " picked" : ""}`} aria-pressed={pinned} disabled={!pinned && !canPin} onClick={onPin}>
            {pinned ? t("classes:unpin") : t("classes:pin")}
          </button>
          <Link to={`/s/${encodeURIComponent(s.share)}`} className="button small">
            <span aria-hidden="true">{t("classes:openShort")} ↗</span>
            <span className="sr-only">{t("classes:openSession")}</span>
          </Link>
        </div>
        {/* what still stands, a line per kind — its sign, what it is, how
            many and when, what it said — and Seen, which takes them off the
            row: where the instructor is looking at the session */}
        {kinds.length > 0 && (
          <div className="watch-alerts" role="group" aria-label={t("session:alerts")}>
            <ul className="watch-alert-list">
              {kinds.map((c) => (
                <li key={c.kind} className={`watch-alert tone-${alertTone(c.kind)}`} data-kind={c.kind}>
                  <AlertIcon kind={c.kind} />
                  <span className="watch-alert-text">
                    <strong>{word("session:alertKind", c.kind) ?? c.kind}</strong>
                    {c.count > 1 ? ` ×${c.count}` : ""} · {ago(c.at, now)}
                    {c.detail && <span className="muted"> · {c.detail}</span>}
                  </span>
                </li>
              ))}
            </ul>
            {/* a permission's wait is its state's, which Seen leaves */}
            {onSeen && kinds.some((c) => !SaidByState.has(c.kind)) && (
              <button type="button" className="small" aria-label={t("session:markSeenOf", { name: s.student.name })} onClick={onSeen}>
                {t("session:markSeen")}
              </button>
            )}
          </div>
        )}
        {/* why Pin cannot be pressed, said where a touch screen sees it too */}
        {!pinned && !canPin && <p className="muted small flush session-watch-why">{t("classes:pinFull", { n: MaxPins })}</p>}
      </header>
      <div className="session-watch-body" {...(hasScreen ? { role: "tabpanel", id: panelId(prefix, shown), "aria-labelledby": tabId(prefix, shown) } : {})}>
        {error ? (
          <p className="error">{t("common:error", { m: error })}</p>
        ) : !share ? (
          <p className="muted">{t("common:loading")}</p>
        ) : (
          // one split for both views, as the share's page has: the timeline
          // stays drawn under the screen, so its folds and where it was
          // scrolled to outlive a tab
          <VSplit
            storageKey="class.watch.split.top"
            label={t("session:splitResize")}
            topLabel={t("session:tabScreen")}
            bottomLabel={t("session:tabTimeline")}
            only={shown === "screen" ? "top" : "bottom"}
            top={
              <Suspense fallback={<p className="muted">{t("common:loading")}</p>}>
                <Screen key={share.id} shareId={share.id} />
              </Suspense>
            }
            bottom={<WatchTimeline share={share} me={me} state={feed.state} />}
          />
        )}
      </div>
      {/* only where the share's policy lets the instructor write */}
      {share && canNote(share.role, policy) && (
        <ChatForm placeholder={t("session:notePlaceholder")} sendLabel={t("session:sendNote")} disabled={!open} why={feed.state.closed !== undefined ? undefined : feed.conn.kind === "connecting" ? t("session:connecting") : feed.conn.kind === "waiting" ? t("session:whyOffline") : undefined} onSend={feed.note} inline className="watch-note" />
      )}
    </section>
  );
}
